"use client";

import Image from "next/image";
import { Controller, useForm } from "react-hook-form";
import cx from "classnames";
import { Link } from "@/i18n/navigation";
import styles from "./index.module.scss";
import Promo from "./promo";
import React, { useCallback, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { useAuthRegister } from "@/domains/auth/hooks/user-auth-register";
import { RegistrationField, RegistrationProps } from "../../types";
import { PhoneInput } from "@/components/UI/PhoneInput/PhoneInput";
import { useTranslations } from "next-intl";
import { countryToCurrency } from "@/utils/country-to-currency";
import { useStore } from "@/hooks/use-store";
import ToggleEmailPhone from "@/components/UI/ToggleEmailPhone";
import { getProfile } from "@/domains/profile/actions";

export default function Registration({ onShowTab }: RegistrationProps) {
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm<RegistrationField>({
    reValidateMode: "onSubmit",
    mode: "all",
  });
  const t = useTranslations("AuthRegistration");
  const tValidation = useTranslations("Validation");
  const isTabletOrMobile = useMediaQuery("only screen and (max-width: 1120px)");
  const [isOpenBonus, setIsOpenBonus] = useState(false);
  const [isOpenPromoCode, setOpenPromoCode] = useState(false);
  const [promoId, setPromoId] = useState<string | null>(null);
  const [country, setCountry] = useState<string>("US");
  const [errorMessage, setErrorMessage] = useState("");
  const { loading, onMutateAuth, renderErrorMessage, error: authError } = useAuthRegister();
  const { modal, user } = useStore();
  const [authType, setAuthType] = useState<"phone" | "email">("phone");

  const [phoneValue, setPhoneValue] = useState("");
  const [emailValue, setEmailValue] = useState("");

  const openSheetBonus = () => setIsOpenBonus(true);
  const closeSheetBonus = () => setIsOpenBonus(false);

  const handleCountryChange = (value: string) => {
    if (value) {
      setCountry(value);
    }
  };

  const onSubmit = useCallback(
    async (data: RegistrationField) => {
      setErrorMessage("");
      try {
        const login = authType === "phone" ? phoneValue.replace("+", "") : emailValue.trim();
        const promoCodeRaw = data.promoCode?.trim();
        const promoCode = promoCodeRaw ? promoCodeRaw : null;

        const currency = countryToCurrency(country.toUpperCase());

        const result = await onMutateAuth({
          ...data,
          login,
          country: country.toUpperCase(),
          type: authType,
          currency,
          promoId,
          promoCode,
          utmParams: user.getUtmParams(),
          isConfirmConditions: data.isConfirmConditions,
        });

        if (!result.success) {
          return;
        }

        const token = result.data?.token;
        const refreshToken = result.data?.refresh_token;

        user.setToken(token || "");
        user.setRefreshToken(refreshToken || "");

        const _user = await getProfile();
        user.setIsAuth(true);
        user.setUser(_user);
        user.setUserBalance(_user.balance.amount);

        reset();
        modal.closeModal();
      } catch (error: any) {
        setErrorMessage(error.message);
      }
    },
    [onMutateAuth, onShowTab],
  );

  return (
    <>
      <div className={styles.main_wrapper}>
        <div className={styles.form_wrapper}>
          <div className={styles.header}>
            <div className={styles.title}>{t("registration")}</div>

            <button className={styles.state_btn} onClick={() => onShowTab("login")}>
              <svg
                data-v-4e9cc203=""
                viewBox="0 0 20 18"
                fill="currentColor"
                width="18"
                height="100%"
                className={styles.state_btn__icon}
              >
                <use href="/images/sprite.svg#login"></use>
              </svg>
              {t("login")}
            </button>
          </div>

          {isTabletOrMobile && (
            <div className={styles.welcome_bonus}>
              <div className={styles.welcome_bonus__box}>
                <div className={styles.welcome_bonus___wrapper}>
                  <Image
                    className={styles.welcome_bonus__img}
                    src={"/images/icons/bonus.png"}
                    width={132}
                    height={132}
                    alt="bonus"
                    quality={100}
                  />
                </div>
              </div>

              <div className={styles.welcome_bonus__title}>
                <span>{t("select_bonus")}</span>
                <Image
                  className={styles.welcome_bonus__title_icon}
                  src="/images/icons/24-arrow.svg"
                  alt=""
                  width={145}
                  height={50}
                  quality={100}
                />
              </div>

              <div className={styles.welcome_bonus__box}>
                <div className={styles.welcome_bonus__img_wrapper}>
                  <Image
                    className={styles.welcome_bonus__img}
                    src={"/images/icons/bonus.png"}
                    width={132}
                    height={132}
                    alt="bonus"
                    quality={100}
                  />
                </div>
              </div>

              <button className={styles.welcome_bonus__btn} onClick={openSheetBonus}></button>
            </div>
          )}

          <ToggleEmailPhone
            onChange={(value) => {
              setAuthType(value);
              setValue("login", value === "phone" ? phoneValue : emailValue);
            }}
          />

          <form
            className={styles.form}
            onSubmit={(e) => {
              handleSubmit(onSubmit)(e);
            }}
          >
            <div className={styles.form_field}>
              {authType === "phone" && (
                <>
                  <label className={cx(styles.label, styles.phone_label)}>
                    <Controller
                      name="login"
                      control={control}
                      rules={{ required: tValidation("required") }}
                      render={({ field: { onChange } }) => (
                        <PhoneInput
                          className={styles.phone_input}
                          autoComplete={"off"}
                          value={phoneValue}
                          onChange={(_phone, _country) => {
                            setPhoneValue(_phone);
                            onChange(_phone);
                            handleCountryChange(_country);
                          }}
                          label={false}
                        />
                      )}
                    />
                  </label>
                  <div className={styles.message}>
                    {renderErrorMessage("phone", authError || [])}
                  </div>
                </>
              )}

              {authType === "email" && (
                <>
                  <label className={styles.label}>
                    <input
                      {...register("login", {
                        required: tValidation("required"),
                        onChange: (e) => setEmailValue(e.target.value),
                      })}
                      className={styles.input}
                      type="email"
                      value={emailValue}
                      placeholder=""
                    />
                    <span className={styles.label_value}>E-mail</span>
                  </label>
                  <div className={styles.message}>
                    {renderErrorMessage("email", authError || [])}
                  </div>
                </>
              )}

              {errors.login && <div className={styles.message}>{errors.login.message}</div>}
              <div className={styles.message}>{renderErrorMessage("login", authError || [])}</div>
              <div className={styles.message}>
                {renderErrorMessage("countryCode", authError || [])}
              </div>
            </div>

            <div className={styles.form_field}>
              <label className={styles.label}>
                <input
                  {...register("password", {
                    required: tValidation("required"),
                    minLength: {
                      value: 6,
                      message: tValidation("password_min_length"),
                    },
                  })}
                  className={styles.input}
                  type="password"
                  placeholder={""}
                />
                <span className={styles.label_value}>{t("password")}</span>
              </label>
              {errors.password && <div className={styles.message}>{errors.password.message}</div>}
              <div className={styles.message}>
                {renderErrorMessage("password", authError || [])}
              </div>
            </div>
            {errorMessage && <div className={styles.error_message}>{errorMessage}</div>}
            <div className={styles.form_field}>
              <button className={styles.btn_submit} type="submit" disabled={loading}>
                {t("submit")}
                {loading && "..."}
              </button>
            </div>

            {isOpenPromoCode ? (
              <div className={`${styles.form_field} ${styles.openPromoCode_field}}`}>
                <label className={styles.label}>
                  <input
                    {...register("promoCode")}
                    className={styles.input}
                    type="text"
                    maxLength={20}
                    placeholder={""}
                  />
                  <span className={styles.label_value}>{t("input_promo_code")}</span>
                </label>
                {errors.promoCode && (
                  <div className={styles.message}>{errors.promoCode.message}</div>
                )}
                <div className={styles.message}>
                  {renderErrorMessage("promoCode", authError || [])}
                </div>
              </div>
            ) : (
              <div
                className={styles.openPromoCode}
                onClick={() => {
                  setOpenPromoCode(true);
                }}
              >
                {t("has_promo_code")}
              </div>
            )}

            <div className={styles.form_field}>
              <label className={styles.label_checkbox}>
                <input
                  className={styles.checkbox}
                  type="checkbox"
                  {...register("isConfirmConditions", {
                    required: tValidation("required"),
                  })}
                />
                <div className={styles.checkbox_pseudo}></div>
                <span className={styles.description}>
                  {t.rich("rulesDesk", {
                    link_1: (chunks) => (
                      <Link
                        href={"/terms-and-conditions"}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {chunks}
                      </Link>
                    ),
                    link_2: (chunks) => (
                      <Link href={"/privacy-policy"} target="_blank" rel="noopener noreferrer">
                        {chunks}
                      </Link>
                    ),
                  })}
                </span>
              </label>

              {errors.isConfirmConditions && (
                <div className={styles.message}>{errors.isConfirmConditions.message}</div>
              )}
            </div>

            <div className={styles.form_field}>
              <label className={styles.label_checkbox}>
                <input
                  className={styles.checkbox}
                  checked
                  type="checkbox"
                  {...register("subscription")}
                />
                <div className={styles.checkbox_pseudo}></div>
                <span className={styles.description}>{t("subscription_notification")}</span>
              </label>

              {errors.subscription && (
                <div className={styles.message}>{errors.subscription.message}</div>
              )}
            </div>
          </form>
        </div>

        <Promo
          setPromoId={setPromoId}
          promoId={promoId}
          isOpen={isOpenBonus}
          onClose={closeSheetBonus}
        />
      </div>
    </>
  );
}
